/**
 * Calculate dynamic background style based on sun position
 */
export const getCelestialBackground = (sunPosition) => {
  if (!sunPosition) {
    return {
      background: '#050914',
      overlay: 'night'
    };
  }

  const { phase, altitude, light_intensity } = sunPosition;

  // Night mode (sun below horizon)
  if (phase === 'night') {
    return {
      background: '#050914',
      overlay: 'night',
      starOpacity: 1
    };
  }

  // Sunrise mode
  if (phase === 'sunrise') {
    return {
      background: 'linear-gradient(to top, #FF7E5F 0%, #FEB47B 40%, #5B96C7 100%)',
      overlay: 'sunrise',
      starOpacity: 0.3
    };
  }

  // Sunset mode
  if (phase === 'sunset') {
    return {
      background: 'linear-gradient(to bottom, #5B96C7 0%, #FEB47B 40%, #FF7E5F 100%)',
      overlay: 'sunset',
      starOpacity: 0.3
    };
  }

  // Day mode (sun above horizon)
  if (phase === 'day') {
    // Interpolate between morning and full day
    const dayIntensity = Math.min(1, altitude / 60);
    const skyBlue = `rgba(91, 150, 199, ${0.5 + dayIntensity * 0.5})`;
    
    return {
      background: `linear-gradient(to bottom, #87CEEB 0%, ${skyBlue} 50%, #5B96C7 100%)`,
      overlay: 'day',
      starOpacity: 0,
      sunOpacity: light_intensity
    };
  }

  return {
    background: '#050914',
    overlay: 'night',
    starOpacity: 1
  };
};

/**
 * Get text colors based on time of day.
 *
 * IMPORTANT: the `accent` colour must always meet a strong contrast ratio
 * against the active background. We never use a light-gold accent on a
 * bright daytime sky — it becomes hard to read. Instead we pick a deep
 * astronomy-themed blue for day, a warm umber for golden hours, and the
 * classic gold for the dark sky.
 */
export const getTextColors = (phase) => {
  if (phase === 'day') {
    return {
      primary: '#050914',
      secondary: '#1A2D42',
      accent: '#0B5394'   // deep sky-blue — high contrast on light background
    };
  }

  if (phase === 'sunrise' || phase === 'sunset') {
    return {
      primary: '#1A0E0A',
      secondary: '#3A2418',
      accent: '#7C2D12'   // warm umber — readable on orange/pink sky
    };
  }

  if (phase === 'civil-twilight' || phase === 'nautical-twilight') {
    return {
      primary: '#FFFFFF',
      secondary: '#C7D2E5',
      accent: '#FCD34D'   // soft gold — readable on indigo twilight
    };
  }

  return {
    primary: '#FFFFFF',
    secondary: '#8BA1C2',
    accent: '#EAEAEA'   // near-white for true night
  };
};

/**
 * Get surface colors based on time of day
 */
export const getSurfaceColors = (phase) => {
  if (phase === 'day') {
    return {
      background: 'rgba(255, 255, 255, 0.4)',
      border: 'rgba(255, 255, 255, 0.3)'
    };
  }
  
  return {
    background: 'rgba(11, 19, 43, 0.4)',
    border: 'rgba(255, 255, 255, 0.1)'
  };
};